import * as _ from 'lodash';
import * as React from 'react';
import { css } from 'aphrodite';
import { hashHistory } from 'react-router';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';

import * as onboardingForm from 'login-utils/onboardingForm';
import * as onboardingRoutingUtils from 'login-utils/onboardingRoutingUtils';
import AutoPurchaseSVG from 'login-components/Common/svg/AutoPurchaseSVG';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import ConstructionSVG from 'login-components/Common/svg/ConstructionSVG';
import HeloanSVG from 'login-components/Common/svg/HeloanSVG';
import HelocSVG from 'login-components/Common/svg/HelocSVG';
import LargeIconButtonGroup from 'login-components/Common/LargeIconButtonGroup/LargeIconButtonGroup';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import MortgageSVG from 'login-components/Common/svg/MortgageSVG';
import States from 'login-constants/states';
import { addNewLoanToBorrower, shouldAddNewLoanToBorrower } from 'login-utils/createLoanUtils';
import { LargeIconButtonConfig } from 'login-components/Common/Buttons/LargeIconButton';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';

interface LoanTypeButtonConfig extends LargeIconButtonConfig {
  value: LoanType;
}

const loanTypeChooserConfigs: LoanTypeButtonConfig[] = [
  {
    value: 'MORTGAGE',
    iconComponent: MortgageSVG,
    buttonId: 'it-chooseLoanTypeMortgage',
    text: 'Mortgage',
    subText: 'Buy a new home or refinance your current one.',
  },
  {
    value: 'HELOAN',
    iconComponent: HeloanSVG,
    buttonId: 'it-chooseLoanTypeHELOAN',
    text: 'Home equity loan',
    subText: 'Borrow a lump sum and pay it back over a fixed term.',
  },
  {
    value: 'HELOC',
    iconComponent: HelocSVG,
    buttonId: 'it-chooseLoanTypeHELOC',
    text: 'Home equity line of credit',
    subText: 'Borrow up to your credit limit for a set time period.',
  },
  {
    value: 'CONSTRUCTION',
    iconComponent: ConstructionSVG,
    buttonId: 'it-chooseLoanTypeConstruction',
    text: 'Construction',
    subText: 'Build a new home or make renovations to an existing home.',
  },
  {
    value: 'AUTO',
    iconComponent: AutoPurchaseSVG,
    buttonId: 'it-chooseLoanTypeAuto',
    text: 'Auto loan',
    subText: 'Buy a car or change your loan terms.',
  },
];

export type ChooseLoanTypeProps = ReactRouter.RouteComponentProps<{}, {}>;

interface ChooseLoanTypeState {
  isSubmitting: boolean;
  error?: string;
}

export class ChooseLoanType extends React.Component<
  ChooseLoanTypeProps & FormProps<DataShape, ChooseLoanTypeProps, ReduxState>,
  ChooseLoanTypeState
> {
  constructor(props) {
    super(props);
    this.state = {
      isSubmitting: false,
    };
  }

  getLoanTypeConfigs = () => {
    const supportedLoanTypes = onboardingRoutingUtils.getSupportedLoanTypes();

    return loanTypeChooserConfigs.filter(({ value }) => _.includes(supportedLoanTypes, value));
  };

  onSelect = async loanType => {
    // Auto loans must go through the normal signup flow to fill out additional signup parameters
    if (loanType !== 'AUTO' && shouldAddNewLoanToBorrower()) {
      this.setState({
        error: '',
        isSubmitting: true,
      });
      // Directly create a new loan for the user if they are logged in
      try {
        await addNewLoanToBorrower({ loanType });
      } catch (err) {
        this.setState({
          error: 'Failed to create application, please try again',
          // Only unset on error, since we navigate away in the success case
          // and don't want the user to be able to submit again
          isSubmitting: false,
        });
      }
    } else {
      // Otherwise, proceed to signup via the borrower onboarding flow
      const { location } = this.props;
      const search = location!.search || '';
      hashHistory.push(`${States.SIGNUP}${search}`);
    }
  };

  render() {
    return (
      <div>
        {this.state.isSubmitting ? (
          <div className={css(paneFormStyles.loadingSpinnerWrapper)}>
            <LoadingSpinner />
          </div>
        ) : (
          <React.Fragment>
            {this.state.error && (
              <div className={css(paneFormStyles.errorWrapper)}>
                <ErrorMessage errorText={this.state.error} />
              </div>
            )}
            <Field
              name={onboardingForm.FIELDS.loanType}
              configs={this.getLoanTypeConfigs()}
              onSelect={this.onSelect}
              component={LargeIconButtonGroup}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

export default reduxForm<FormProps<DataShape, ChooseLoanTypeProps, ReduxState>, ChooseLoanTypeProps, ReduxState>(
  onboardingForm.FORM_CONFIG,
)(ChooseLoanType);
